<template>
  <div ref="timelineRef" class="ui-timeline" :class="timelineClasses">
    <div class="ui-timeline__container">
      <div class="ui-timeline__periods">
        <Periods
          ref="periodsRef"
          v-model="periodsModel"
          :size
          :disabled
          @blur="handleBlur"
          @select="handlePeriodsSelect"
        />
        <Freq
          ref="freqRef"
          v-model="freqModel"
          :size
          :disabled
          @blur="handleBlur"
          @select="handleFreqSelect"
        />
      </div>
      <div class="ui-timeline__end">
        <div class="ui-timeline__label">Periods <br />Ending:</div>
        <End
          v-model="endModel"
          v-model:end-is-today="endIsTodayModel"
          :size
          :disabled
          @update="handleUpdateEnd"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, nextTick, reactive, ref, useTemplateRef, watch } from 'vue'
import { isEqual } from 'lodash'
import { useResizeObserver } from '@vueuse/core'

import { RepositoryTimeline } from '@types'

import { getCurrentDate } from '@/helpers/dates'

import End from './components/End.vue'
import Freq from './components/Freq.vue'
import Periods from './components/Periods.vue'

const SUBMIT_DELAY = 300

type Props = {
  timeline: RepositoryTimeline
  dates: string[]
  disabled?: boolean
}

type Emits = {
  'update:timeline': [data: RepositoryTimeline, isEndChanged?: boolean]
  'update:end-is-today': [data: boolean]
}

const props = defineProps<Props>()
const emit = defineEmits<Emits>()

defineOptions({
  name: 'UITimeline',
})

const periodsRef = useTemplateRef('periodsRef')
const freqRef = useTemplateRef('freqRef')
const timelineRef = useTemplateRef('timelineRef')

const isShortMode = ref(false)

const isSelectStarted = ref(false)
let submitTimeout: NodeJS.Timeout

const internalTimeline = reactive<Partial<Pick<RepositoryTimeline, 'periods' | 'freq' | 'end' | 'end_is_today'>>>({
  periods: undefined,
  freq: undefined,
  end: undefined,
  end_is_today: undefined,
})

const periodsModel = computed({
  get() {
    return internalTimeline.periods || props.timeline.periods || 1
  },
  set(value) {
    internalTimeline.periods = value
  },
})

const freqModel = computed({
  get() {
    return internalTimeline.freq || props.timeline.freq || 'day'
  },
  set(value) {
    internalTimeline.freq = value
  },
})

const endModel = computed({
  get() {
    if (internalTimeline.end) return internalTimeline.end
    return props.timeline.end || getCurrentDate({})
  },
  set(value) {
    internalTimeline.end = value
  },
})

const endIsTodayModel = computed({
  get() {
    if (internalTimeline.end_is_today !== undefined) {
      return internalTimeline.end_is_today
    }
    return props.timeline.end_is_today || false
  },
  set(value) {
    internalTimeline.end_is_today = value
  },
})

useResizeObserver(timelineRef, entries => {
  isShortMode.value = entries[0]?.target?.parentElement
    ? entries[0]?.target?.parentElement?.clientWidth < 450
    : false
})

const size = computed(() => (isShortMode.value ? 'sm' : undefined))

const timelineClasses = computed(() => ({
  'ui-timeline--short': isShortMode.value,
}))

const newTimelineValue = computed(() => ({
  ...props.timeline,
  ...internalTimeline,
}))

const handleUpdate = (isEndChanged?: boolean) => {
  if (!props.timeline) return
  if (isEqual(newTimelineValue.value, props.timeline) && !isEndChanged) return
  emit('update:timeline', newTimelineValue.value, isEndChanged)
}

const handleUpdateEnd = () => {
  handleUpdate(true)
}

const handleBlur = () => {
  if (submitTimeout) {
    clearTimeout(submitTimeout)
  }

  if (isSelectStarted.value) {
    submitTimeout = setTimeout(() => {
      handleUpdate()
      isSelectStarted.value = false
    }, SUBMIT_DELAY)
  }
}

const handlePeriodsSelect = () => {
  if (!isSelectStarted.value) {
    freqRef.value?.activate()
    nextTick(() => {
      isSelectStarted.value = true
    })
  } else {
    handleBlur()
  }
}

const handleFreqSelect = () => {
  if (!isSelectStarted.value) {
    periodsRef.value?.activate()
    nextTick(() => {
      isSelectStarted.value = true
    })
  } else {
    handleBlur()
  }
}

watch(endIsTodayModel, value => {
  emit('update:end-is-today', value)
})
</script>

<style>
.ui-timeline {
  &__container {
    @apply flex items-end;
    @apply gap-x-2;
  }

  &__periods {
    @apply flex items-center;
    @apply gap-x-2;
  }

  &__end {
    @apply flex items-center;
    @apply gap-x-2;
  }

  &--short &__container,
  &--short &__end,
  &--short &__periods {
    @apply gap-x-1;
  }

  &__label {
    @apply text-sm sm:text-xs;
    @apply leading-4;
    @apply text-gray-500 dark:text-gray-400;
    @apply uppercase;
  }

  &--short &__label {
    @apply text-xs sm:text-[0.65rem];
    @apply leading-tight;
  }
}
</style>
